/**
 * Hebrew Language Profile
 *
 * SVO word order, prepositions, RTL (right-to-left), space-separated.
 * Hebrew uses prefix prepositions (ב, ל, מ, כ) that attach to the following word.
 * Features optional vowel points (nikkud) which are typically omitted in modern text.
 */

import type { LanguageProfile } from './types';

export const hebrewProfile: LanguageProfile = {
  code: 'he',
  name: 'Hebrew',
  nativeName: 'עברית',
  direction: 'rtl',
  script: 'hebrew',
  wordOrder: 'SVO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  verb: {
    position: 'second', // SVO - verb comes after subject
    subjectDrop: true,
  },
  references: {
    me: 'אני', // "I/me"
    it: 'זה', // "it" (masculine) / "this"
    you: 'אתה', // "you" (masculine singular)
    result: 'תוצאה',
    event: 'אירוע',
    target: 'יעד',
    body: 'גוף',
  },
  possessive: {
    marker: 'של', // "of" - used for general possession
    markerPosition: 'before-property',
    usePossessiveAdjectives: true,
    specialForms: {
      // Hebrew: "property שלי" (property of-me) - possessive follows property
      me: 'שלי', // "of me" / "mine"
      it: 'שלו', // "of it" / "its" (masculine)
      you: 'שלך', // "of you" / "yours"
    },
    keywords: {
      // "my" / "mine"
      שלי: 'me',
      // "your" / "yours"
      שלך: 'you',
      // "its/his/her"
      שלו: 'it', // his/its (masculine)
      שלה: 'it', // her/its (feminine)
    },
  },
  roleMarkers: {
    destination: { primary: 'על', alternatives: ['ב', 'אל', 'ל'], position: 'before' },
    source: { primary: 'מ', alternatives: ['מן', 'מאת'], position: 'before' },
    patient: { primary: 'את', position: 'before' }, // Direct object marker
    style: { primary: 'עם', alternatives: ['ב'], position: 'before' },
  },
  keywords: {
    // Class/Attribute operations
    toggle: { primary: 'מתג', alternatives: ['הדלק כבה'], normalized: 'toggle' },
    add: { primary: 'הוסף', normalized: 'add' },
    remove: { primary: 'הסר', alternatives: ['מחק', 'הורד'], normalized: 'remove' },
    // Content operations
    put: { primary: 'שים', alternatives: ['הנח', 'הכנס'], normalized: 'put' },
    append: { primary: 'צרף', alternatives: ['הוסף בסוף'], normalized: 'append' },
    prepend: { primary: 'הקדם', alternatives: ['הוסף בהתחלה'], normalized: 'prepend' },
    take: { primary: 'קח', normalized: 'take' },
    make: { primary: 'צור', alternatives: ['הכן'], normalized: 'make' },
    clone: { primary: 'שכפל', normalized: 'clone' },
    swap: { primary: 'החלף', alternatives: ['המר'], normalized: 'swap' },
    morph: { primary: 'הפוך', alternatives: ['עצב מחדש'], normalized: 'morph' },
    // Variable operations
    set: { primary: 'קבע', alternatives: ['הגדר'], normalized: 'set' },
    get: { primary: 'קבל', alternatives: ['השג'], normalized: 'get' },
    increment: { primary: 'הגדל', alternatives: ['הוסף אחד'], normalized: 'increment' },
    decrement: { primary: 'הקטן', alternatives: ['הפחת'], normalized: 'decrement' },
    log: { primary: 'רשום', normalized: 'log' },
    // Visibility
    show: { primary: 'הראה', alternatives: ['הצג'], normalized: 'show' },
    hide: { primary: 'הסתר', alternatives: ['החבא'], normalized: 'hide' },
    transition: { primary: 'מעבר', alternatives: ['הנפש'], normalized: 'transition' },
    // Events
    // NOTE: עם is he's WITH/style marker, NOT an event word — listing it as an
    // `on` alternative anchored phantom handlers on `עם method:"POST"` tails.
    on: { primary: 'ב', normalized: 'on' },
    trigger: { primary: 'הפעל', alternatives: ['שגר'], normalized: 'trigger' },
    send: { primary: 'שלח', normalized: 'send' },
    // DOM focus
    focus: { primary: 'מקד', alternatives: ['התמקד'], normalized: 'focus' },
    blur: { primary: 'טשטש', alternatives: ['הסר מיקוד'], normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'רוקן', normalized: 'empty' },
    open: { primary: 'פתח', normalized: 'open' },
    close: { primary: 'סגור', normalized: 'close' },
    select: { primary: 'סמן', normalized: 'select' },
    clear: { primary: 'נקה', normalized: 'clear' },
    reset: { primary: 'אפס', normalized: 'reset' },
    // The i18n he dictionary has no breakpoint entry — the transformer passes
    // the en loanword through verbatim, so parsing must accept it alongside
    // the native compound.
    breakpoint: { primary: 'נקודת-עצירה', alternatives: ['breakpoint'], normalized: 'breakpoint' },
    // Navigation
    go: { primary: 'לך', alternatives: ['נווט'], normalized: 'go' },
    scroll: { primary: 'גלול', normalized: 'scroll' },
    push: { primary: 'דחוף', alternatives: ['הוסף-להיסטוריה'], normalized: 'push' },
    replace: { primary: 'החלף_כתובת', alternatives: ['שנה_כתובת'], normalized: 'replace' },
    process: { primary: 'עבד', alternatives: ['עיבוד'], normalized: 'process' },
    // Async
    wait: { primary: 'חכה', alternatives: ['המתן'], normalized: 'wait' },
    fetch: { primary: 'הבא', alternatives: ['טען'], normalized: 'fetch' },
    settle: { primary: 'התייצב', normalized: 'settle' },
    // Control flow
    if: { primary: 'אם', normalized: 'if' },
    unless: { primary: 'אלא', normalized: 'unless' },
    when: { primary: 'כאשר', alternatives: ['כש'], normalized: 'when' },
    // Reactive `when <expr> changes` trigger word — synced VERBATIM from the i18n
    // dictionary (`changes`), which is what wrote every stored corpus row; the V1
    // vocab gate requires the two surfaces to agree. Native review pending — see
    // NATIVE_REVIEW_NEEDED.md § "Reactive `when … changes`".
    changes: { primary: 'משתנה', normalized: 'changes' },
    where: { primary: 'איפה', alternatives: ['היכן'], normalized: 'where' },
    else: { primary: 'אחרת', alternatives: ['אם לא'], normalized: 'else' },
    repeat: { primary: 'חזור', normalized: 'repeat' },
    for: { primary: 'עבור', alternatives: ['לכל'], normalized: 'for' },
    while: { primary: 'כל עוד', alternatives: ['בזמן'], normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'forever', normalized: 'forever' },
    continue: { primary: 'המשך', normalized: 'continue' },
    halt: { primary: 'עצור', alternatives: ['הפסק'], normalized: 'halt' },
    throw: { primary: 'זרוק', normalized: 'throw' },
    call: { primary: 'קרא', alternatives: ['זמן'], normalized: 'call' },
    return: { primary: 'החזר', normalized: 'return' },
    then: { primary: 'אז', alternatives: ['ואז'], normalized: 'then' },
    and: { primary: 'וגם', alternatives: ['גם'], normalized: 'and' },
    end: { primary: 'סוף', alternatives: ['סיום'], normalized: 'end' },
    // Advanced
    js: { primary: 'js', alternatives: ['ג׳אווהסקריפט'], normalized: 'js' },
    async: { primary: 'אסינכרוני', normalized: 'async' },
    tell: { primary: 'אמור', normalized: 'tell' },
    default: { primary: 'ברירת מחדל', normalized: 'default' },
    init: { primary: 'אתחל', alternatives: ['התחל'], normalized: 'init' },
    behavior: { primary: 'התנהגות', normalized: 'behavior' },
    install: { primary: 'התקן', normalized: 'install' },
    measure: { primary: 'מדוד', normalized: 'measure' },
    beep: { primary: 'ביפ', normalized: 'beep' },
    break: { primary: 'שבור', normalized: 'break' },
    copy: { primary: 'העתק', normalized: 'copy' },
    exit: { primary: 'צא', normalized: 'exit' },
    pick: { primary: 'בחר', normalized: 'pick' },
    render: { primary: 'רנדר', normalized: 'render' },
    // Modifiers
    into: { primary: 'לתוך', alternatives: ['אל'], normalized: 'into' },
    before: { primary: 'לפני', normalized: 'before' },
    after: { primary: 'אחרי', normalized: 'after' },
    // Common event names (for event handler patterns)
    click: { primary: 'לחיצה', alternatives: ['קליק'], normalized: 'click' },
    hover: { primary: 'ריחוף', normalized: 'hover' },
    submit: { primary: 'שליחה', alternatives: ['הגשה'], normalized: 'submit' },
    input: { primary: 'קלט', alternatives: ['הזנה'], normalized: 'input' },
    change: { primary: 'שינוי', alternatives: ['עדכון'], normalized: 'change' },
    // Event modifiers (for repeat until event)
    until: { primary: 'עד', normalized: 'until' },
    event: { primary: 'אירוע', normalized: 'event' },
    from: { primary: 'מ', alternatives: ['מאת'], normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-התחבר`, `hx-חי`, etc.
    connect: { primary: 'התחבר', alternatives: ['חבר', 'התחברות'], normalized: 'connect' },
    stream: { primary: 'הזרם', alternatives: ['סטרים', 'שידור'], normalized: 'stream' },
    live: { primary: 'חי', alternatives: ['בזמן-אמת', 'לייב'], normalized: 'live' },
    socket: { primary: 'שקע', alternatives: ['סוקט', 'ווב-סוקט', 'socket'], normalized: 'socket' },
    // Reactive / realtime commands
    bind: { primary: 'קשור', alternatives: ['אגד', 'bind'], normalized: 'bind' },
    intercept: { primary: 'יירט', alternatives: ['יירוט', 'intercept'], normalized: 'intercept' },
    worker: { primary: 'עובד', alternatives: ['worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['מקור-אירועים'],
      normalized: 'eventsource',
    },
  },
  tokenization: {
    prefixes: ['ה', 'ו', 'ב', 'כ', 'ל', 'מ', 'ש'], // Common Hebrew prefixes
  },
  eventHandler: {
    keyword: { primary: 'ב', normalized: 'on' },
    sourceMarker: { primary: 'מ', alternatives: ['מאת'], position: 'before' },
    conditionalKeyword: { primary: 'כאשר', alternatives: ['כש', 'אם'] },
    // Event marker: ב (at/upon), used in SVO pattern
    // Pattern: ב [event] [verb] [patient] על [destination?]
    // Example: בלחיצה החלף .active על #button
    eventMarker: { primary: 'ב', alternatives: ['כש'], position: 'before' },
    temporalMarkers: ['כאשר', 'כש'], // temporal conjunctions (when); עם is the WITH marker, not temporal
  },
};
